<template>
  <div>
    <div class="creditcard-wrap">
      <div class="box">
        <h2 class="title">{{ $t("userCards.header.title") }}</h2>

        <div v-if="cards.length > 0">
          <credit-card
            v-for="card in cards"
            :key="card.id"
            :data="card"
            @destroy="confirm"
          ></credit-card>
        </div>
        <div v-else>{{ $t("userCards.noResults.title") }}</div>

        <button class="button is-icon is-add" @click="add">
          {{ $t("global.register2") }}
        </button>
      </div>

      <zul-modal
        id="add-user-card"
        ref="modal"
        :title="$t('global.registerCard')"
        @primary="save"
      >
        <div v-if="isLoading">
          <p class="loading historic-loading">{{ $t("global.loading2") }}</p>
        </div>

        <template v-else-if="!card.type">
          <zul-button class="is-large" @click="chooseType('credit')">
            <span slot="icon" class="icon is-card"></span>
            {{ $t("global.credit") }}
          </zul-button>
          <zul-button class="is-large" @click="chooseType('debit')">
            <span slot="icon" class="icon is-card"></span>
            {{ $t("global.debit") }}
          </zul-button>
        </template>
        <template v-else-if="card.type === 'debit' && !debitIssuer">
          <h2 class="subtitle">{{ $t("global.bankAsk") }}</h2>
          <zul-button
            class="is-large"
            @click="chooseIssuer(issuer)"
            v-for="issuer in debitIssuers"
            :key="issuer.id"
            :disabled="!issuer.enabled"
          >
            <span slot="icon"
              ><img :src="issuer.image['@1x']" width="40"
            /></span>
            {{ issuer.name }}
          </zul-button>

          <p class="is-note">
            {{ $t("userCards.note.message") }}
          </p>
        </template>
        <fieldset v-else class="form">
          <zul-input
            type="text"
            v-model="card.name"
            name="name"
            icon="buddy"
            v-validate="'required|alpha_spaces'"
            :label="$t('global.form.cardName.title')"
          ></zul-input>
          <zul-input
            type="tel"
            v-model="card.number"
            name="number"
            icon="wallet"
            v-validate="'required'"
            :label="$t('global.form.cardNumber.title')"
            :mask="creditCardMask()"
          ></zul-input>
          <zul-input
            type="tel"
            v-model="card.expires"
            name="expires"
            icon="calendar"
            v-validate="'required|date_format:MM/yyyy'"
            :label="$t('global.form.cardExpires.title')"
            mask="##/####"
          ></zul-input>
          <zul-input
            type="tel"
            v-model="card.cvv"
            name="cvv"
            icon="lock"
            v-validate="'required|numeric|length:3,4'"
            :label="$t('global.form.cardCvv.title')"
            mask="####"
          ></zul-input>
          <zul-input
            type="tel"
            v-model="card.document"
            name="document"
            icon="document"
            v-validate="'required|cpf'"
            label="CPF"
            :mask="'###.###.###-##'"
          ></zul-input>
        </fieldset>
      </zul-modal>

      <zul-modal
        ref="deleteModal"
        :title="$t('userCards.delete.header.title')"
        :primary="$t('userCards.delete.buttonRemove')"
        :secondary="$t('userCards.delete.buttonCancel')"
        @primary="remove"
        @secondary="cancel"
      >
        <p v-if="message">{{ message }}</p>
        <p v-else>{{ $t("userCards.delete.ask") }}</p>
      </zul-modal>
    </div>

    <card-verification ref="cardVerificationModal"></card-verification>
    <ThreeDsAuth ref="threeDsAuth" />
  </div>
</template>

<script>
import config from "config";
import AdyenEncrypt from "adyen-cse-js";
import { getBrowserInfo, OrderStatuses } from "@zuldigital/classic-adyen-3ds";

import ZulButton from "../../components/Button.vue";
import CreditCard from "../../components/CreditCard.vue";
import CardVerification from "../../modals/CardVerification.vue";
import ThreeDsAuth from "../../modals/ThreeDsAuth";

export default {
  props: {},
  data() {
    return {
      card: {},
      cards: this.$auth.user().paymentMethods || [],
      debitIssuer: null,
      debitIssuers: [],
      message: null,
      isLoading: false,
    };
  },
  mounted() {
    this.$root.$on("add-user-card", (type) => {
      this.add(type);
    });
  },
  methods: {
    add(type) {
      // reset data
      Object.assign(this.$data, this.$options.data.apply(this));

      const user = this.$auth.user();
      this.$refs.modal.setProps({ labels: { primary: false } });

      // set default
      this.card = {
        name: user.name,
        document: user.document,
      };

      if (typeof type === "string") {
        this.chooseType(type);
      }

      this.$refs.modal.open();
    },

    chooseType(type) {
      if (type === "debit") {
        this.isLoading = true;

        this.$http
          .get("/order/debit/issuers?action=ADD_PAYMENT")
          .then((res) => {
            this.isLoading = false;
            this.debitIssuers = res.data.items;
          });
      } else {
        this.$refs.modal.setProps({
          labels: { primary: this.$t("global.register2") },
        });
      }

      this.card = Object.assign({ type: type }, this.card);
    },

    chooseIssuer(issuer) {
      this.debitIssuer = issuer;
      this.$refs.modal.setProps({
        labels: { primary: this.$t("global.register2") },
      });
    },

    save() {
      this.$refs.modal.startLoading();

      const FINGERPRINT_POST_BACK_URL = `${config.API_URL.replace(
        "/v7",
        ""
      )}/external/payment/3ds/challenge`;

      const checkGatewayURI =
        "/order/credit/gateway?bin=" +
        this.card.number.replace(/ /g, "").substr(0, 6);
      let token;

      this.$http
        .get(checkGatewayURI)
        .then(async (res) => {
          if (res.data.gateway === "ADYEN") {
            token = this.createAdyenCardToken(this.card);
          } else if (res.data.gateway === "MERCADOPAGO") {
            token = await this.createMPCardToken(this.card).then((res) => {
              return res.data.id;
            });
          }

          const data = new FormData();

          data.append("token", token);
          data.append("gateway", res.data.gateway);

          data.append(
            "cardNumberLength",
            this.card.number.replace(/ /g, "").length
          );

          data.append("cardHolderName", this.card.name);
          data.append(
            "cardHolderDocument",
            this.card.document.replace(/\D/g, "")
          );
          data.append("bin", this.card.number.replace(/\s/g, "").slice(0, 6));

          data.append("notificationURL", FINGERPRINT_POST_BACK_URL);

          data.append("category", "CAD");

          data.append("browserInfo", JSON.stringify({ ...getBrowserInfo() }));

          this.$http
            .post("user/payment/" + this.card.type, data)
            .then((res) => {
              this.cards.push(res.data);

              if (res.data.requiresVerification) {
                this.$refs.cardVerificationModal.open(res.data).then(() => {
                  this.$auth.fetch({
                    success: (res) => {
                      this.$auth.user(res.data);
                      this.cards.push(res.data?.paymentMethods);

                      this.$refs.modal.stopLoading();
                      this.$refs.modal.close();
                      this.card = {};
                    },
                  });
                });
              } else {
                this.$auth.fetch({
                  success: (res) => {
                    this.$auth.user(res.data);

                    this.$refs.modal.stopLoading();
                    this.$refs.modal.close();
                    this.card = {};
                  },
                });
              }
            })
            .catch((error) => {
              if (
                error?.response?.status === 426 &&
                [
                  OrderStatuses.AUTH_CHALLENGE,
                  OrderStatuses.AUTH_FINGERPRINT,
                ].includes(error?.response?.data?.data?.order?.status || "")
              ) {
                const { id, category, status } = error.response.data.data.order;

                this.$refs.modal.stopLoading();
                this.$refs.modal.close();

                this.$refs.threeDsAuth
                  .open({
                    id,
                    category,
                    status,
                  })
                  .then((cardResponse) => {
                    if (cardResponse?.data?.paymentMethod) {
                      this.cards.push(cardResponse?.data?.paymentMethod);
                    }

                    this.$auth.fetch({
                      success: (res) => {
                        this.$auth.user(res.data);
                        this.card = {};

                        this.cards = res.data.paymentMethods;

                        this.$refs.threeDsAuth.close();
                      },
                    });
                  })
                  .catch((error) => {
                    this.$buefy.toast.open({
                      message:
                        typeof error === "string"
                          ? error
                          : "Erro ao autenticar cartão.",
                      type: "is-danger",
                    });
                  });
              } else {
                let message =
                  error.response?.data?.message ||
                  this.$t("userCards.error.default.message");

                if (error?.response.status === 409) {
                  message = this.$t("userCards.error.409.message");
                }

                this.$buefy.toast.open({
                  message,
                  type: "is-danger",
                });
              }
            })
            .finally(() => {
              this.$refs.modal.stopLoading();
            });
        })
        .catch((error) => {
          let message =
            error.response?.data?.message ||
            this.$t("userCards.error.default.message");

          this.$buefy.toast.open({
            message,
            type: "is-danger",
          });
        })
        .finally(() => {
          this.$refs.modal.startLoading();
        });
    },
    confirm(id) {
      this.card.id = id;
      this.message = null;
      this.$refs.deleteModal.setProps({
        labels: { primary: this.$t("global.remove") },
      });
      this.$refs.deleteModal.open();
    },
    cancel() {
      this.card = {};
      this.$refs.deleteModal.close();
    },
    remove() {
      this.$refs.deleteModal.startLoading();
      this.message = null;

      this.$http.delete(`user/payment/${this.card.id}`).then(
        (res) => {
          if (res.status === 200) {
            this.cards = this.cards.filter((o) => o.id != this.card.id);

            this.$auth.fetch({
              success: (res) => {
                this.$auth.user(res.data);

                this.$refs.deleteModal.stopLoading();
                this.$refs.deleteModal.close();
              },
            });
          }
        },
        (err) => {
          this.$refs.deleteModal.stopLoading();
          this.message = err.response.data.message;
          this.$refs.deleteModal.setProps({ labels: { primary: null } });
        }
      );
    },
    createMPCardToken(card) {
      const exp = card.expires.split("/");

      return this.MP.post(`/v1/card_tokens?public_key=${this.MP.publicKey}`, {
        cardNumber: card.number.replace(/ /g, ""),
        securityCode: card.cvv,
        expirationMonth: exp[0],
        expirationYear: exp[1],
        cardholder: {
          identification: {
            type: "CPF",
            number: card.document.replace(/[./-]/g, ""),
          },
          name: card.name,
        },
      });
    },

    createAdyenCardToken(card) {
      const cseInstance = AdyenEncrypt.createEncryption(
        config.ADYEN_PUBLIC_KEY,
        {}
      );
      const exp = card.expires.split("/");

      const cardData = {
        number: card.number,
        cvc: card.cvv,
        holderName: card.name,
        expiryMonth: exp[0],
        expiryYear: exp[1],
        generationtime: new Date().toISOString(),
      };

      return cseInstance.encrypt(cardData);
    },

    creditCardMask() {
      return this.card.type === "debit"
        ? "#### #### #### #### ##"
        : "#### #### #### ####";
    },
  },
  components: {
    ZulButton,
    CreditCard,
    CardVerification,
    ThreeDsAuth,
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
.creditcard-wrap {
  height: 100%;
  padding-bottom: 1px;
}

p.is-note {
  font-size: 13px;
}

.box {
  margin-right: 1px;
  margin-bottom: 1px !important;
}

.button {
  position: absolute;
  right: 24px;
  top: 24px;
  opacity: 0.5;
}
</style>
